import BaseApiService from './BaseApiService'

/** 'docmorphia' means the file is already in DocMorphia syntax and needs no conversion. */
export type MigrateFormat = 'docmorphia' | 'carbone' | 'docmosis' | 'handlebars'

export interface InspectedMoniker {
  rawName: string
  normalizedKey: string
  kind: 'Scalar' | 'Loop' | 'Conditional'
  occurrenceCount: number
  /** The converted formatter chain as written in the template, e.g. "currency:USD". */
  formatters: string | null
  /** What that chain produces for the value the preview uses. */
  sample: string | null
}

export interface ClashSpelling {
  rawName: string
  approxPage: number
  sectionIndex: number
  paragraphIndex: number
  contextSnippet: string
}

export interface MonikerClashError {
  normalizedKey: string
  conflictingSpellings: ClashSpelling[]
}

export interface DocxAnalysis {
  fileName: string
  /** What the detector found, independent of any override. Null means DocMorphia syntax. */
  detectedVendor: Exclude<MigrateFormat, 'docmorphia'> | null
  hint: string | null
  /** The format actually applied to produce these monikers. */
  effectiveVendor: MigrateFormat
  monikers: InspectedMoniker[]
  conversionChanges: string[]
  warnings: string[]
  clashErrors: MonikerClashError[]
}

class MigrateApiService extends BaseApiService {
  /**
   * Detects the source format, converts, and reports the resulting monikers — without persisting
   * anything. Pass `format` to override the detection.
   */
  analyzeDocx(file: File, format?: MigrateFormat, onProgress?: (pct: number) => void): Promise<DocxAnalysis> {
    const form = new FormData()
    form.append('file', file)
    if (format) form.append('format', format)
    return this.postFormWithProgress('/v1/migrate/analyze-docx', form, onProgress)
  }

  /** Renders the converted document with placeholder values so the layout can be eyeballed. */
  previewDocx(file: File, format?: MigrateFormat, outputFormat = 'pdf'): Promise<Blob> {
    const form = new FormData()
    form.append('file', file)
    if (format) form.append('format', format)
    form.append('outputFormat', outputFormat)
    return this.postFormBlobWithProgress('/v1/migrate/preview-docx', form)
  }
}

export const migrateApiService = new MigrateApiService()
